import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Form } from 'react-bootstrap'
import Select from 'react-select'
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarAlt,
  faEdit,
  faHistory,
  faPlus,
  faToggleOff,
  faToggleOn,
  faTrashAlt,
} from '@fortawesome/pro-duotone-svg-icons'
import { PermissionGate } from '../../../../components/rbac'
import GlobalStyles from '../../../../components/component-items/styles'
import { TableCell } from '../../../../components/component-items/datatable'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import CardLoading from '../../../../components/component-items/loading-popover'
import { notify, useDelete, useFetch, usePost, usePut } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'
import {
  ScheduleCalendar,
  ScheduleCalendarSlot,
  ScheduleCalendarTimezoneMode,
} from '../../../../components/component-items/schedule-calendar'
import { usePermissions } from '../../../../hooks/usePermissions'
import { WaveScheduleDrawer } from './wave-schedule-drawer'
import { WaveScheduleHistory } from './wave-schedule-history'
import {
  WaveScheduleCalendarEntry,
  WaveSchedule,
  WaveScheduleDraft,
  WaveScheduleOccurrence,
  WaveScheduleTargetOption,
} from './wave-schedule-types'
import {
  buildCalendarEntries,
  buildWaveScheduleTargetId,
  buildWaveScheduleListUrl,
  convertDisplaySlotToWarehouse,
  formatDateTime,
  formatRecurrence,
  getCurrentMinutesInTimezone,
  getDisplayTimezone,
  getTodayDateInTimezone,
  isWarehouseSlotInPast,
  parseDateTimeInTimezone,
  getViewerTimezone,
  normalizeTargetsFromProfiles,
  removeManualSkipOccurrence,
  ScheduleTimeDisplayMode,
  scheduleStatusConfig,
  summarizeTargets,
} from './wave-schedule-utils'

type CompanyFilterOption = {
  value: string
  label: string
}

type ScheduleFilterOption = {
  value: number
  label: string
}

const formatScheduleWindow = (schedule: Pick<WaveSchedule, 'start_date' | 'end_date'>) => {
  if (!schedule.start_date && !schedule.end_date) return 'Open-ended'
  if (schedule.start_date && schedule.end_date) return `${schedule.start_date} - ${schedule.end_date}`
  if (schedule.start_date) return `Starts ${schedule.start_date}`
  return `Ends ${schedule.end_date}`
}

const getLatestOutcome = (schedule: WaveSchedule) => {
  const downstreamStatus = schedule.last_downstream_run?.status
  if (downstreamStatus === 'failed') return { label: 'Failed', tone: 'red' as const }
  if (downstreamStatus === 'running') return { label: 'Running', tone: 'blue' as const }
  if (downstreamStatus === 'queued') return { label: 'Queued', tone: 'blue' as const }
  if (downstreamStatus === 'completed') return { label: 'Completed', tone: 'green' as const }

  return schedule.last_launch_status ? scheduleStatusConfig[schedule.last_launch_status] : null
}

const DashboardLayout = styled.div`
  padding: 0 1rem 2rem;
  color: #293241;

  .page-header {
    padding-left: 0;
    padding-right: 0;
  }

  .page-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 0;
    position: relative;
    z-index: 10;
  }

  .filter-field {
    flex: 0 1 360px;
    min-width: 280px;
    width: 360px;
  }

  button {
    flex: 0 0 auto;
  }

  .table-actions {
    display: flex;
    gap: 0.75rem;
    align-items: flex-end;
    justify-content: flex-end;
  }

  .table-card {
    margin-top: 1rem;
    border: 1px solid #d9e2f0;
    border-radius: 10px;
    background: #ffffff;
    overflow: hidden;
    box-shadow: 0 14px 30px rgba(34, 46, 84, 0.07);
  }

  .table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.1rem;
    border-bottom: 1px solid #dfe7f3;
    background: linear-gradient(180deg, #fbfcff 0%, #f6f8fc 100%);
  }

  .table-title {
    h4 {
      margin: 0;
      color: #293241;
      font-size: 1rem;
      font-weight: 800;
    }

    p {
      margin: 0.3rem 0 0;
      color: #687386;
      font-size: 0.84rem;
    }
  }

  .schedule-name {
    color: #293241;
    font-weight: 800;
  }

  .schedule-name-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
  }

  .schedule-meta,
  .target-meta {
    margin-top: 0.3rem;
    color: #687386;
    font-size: 0.82rem;
    line-height: 1.4;
  }

  .target-list {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .target-scroll {
    display: grid;
    gap: 0.3rem;
    max-height: 138px;
    overflow-y: auto;
    padding: 0.1rem;
    border-radius: 8px;
    box-shadow: rgba(67, 56, 93, 0.09) -4px 4px 15px 1px;
    background: #ffffff;
  }

  .target-item {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    padding: 0.4rem 0.5rem;
    border: 1px solid #e7eef9;
    border-radius: 7px;
    background: #f8fbff;
  }

  .target-index {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #e8f0ff;
    color: #2457b8;
    font-size: 0.68rem;
    font-weight: 800;
    flex: 0 0 auto;
    margin-top: 1px;
  }

  .target-item-body {
    min-width: 0;
  }

  .target-item-name {
    color: #293241;
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .target-type-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.12rem 0.46rem;
    border: 1px solid transparent;
    font-size: 0.67rem;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    margin-top: 0.22rem;
  }

  .target-type-pill.profile {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }

  .target-type-pill.directive {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #0f172a;
  }

  .next-run {
    font-weight: 800;
    color: #1f2a3d;
  }

  .next-run-muted {
    color: #94a3b8;
    font-weight: 700;
  }

  .outcome-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.24rem 0.64rem;
    background: #f1f5f9;
    color: #475569;
    font-size: 0.78rem;
    font-weight: 800;
    line-height: 1.2;
  }

  .outcome-pill.green {
    background: #ecfdf5;
    color: #047857;
  }

  .outcome-pill.red {
    background: #fef2f2;
    color: #b91c1c;
  }

  .outcome-pill.yellow {
    background: #fffbeb;
    color: #b45309;
  }

  .outcome-pill.blue {
    background: #eff6ff;
    color: #2457b8;
  }

  .outcome-pill.silver {
    background: #f8fafc;
    color: #64748b;
  }

  .action-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.45rem;
  }

  .action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    border: 1px solid #d8e0ed;
    border-radius: 6px;
    background: #ffffff;
    color: #3f4b5f;
    padding: 0.35rem 0.6rem;
    font-size: 0.82rem;
    font-weight: 800;
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease;
  }

  .action-button:hover {
    border-color: #7aa7f8;
    background: #f3f7ff;
    color: #2457b8;
  }

  table thead th {
    background: #fbfcff;
    color: #687386;
    font-size: 0.78rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  tbody tr {
    transition: background 0.15s ease;
  }

  tbody tr:nth-child(even) {
    background: #fcfdff;
  }

  tbody tr:hover {
    background: #f7faff;
  }

  .empty-state {
    padding: 2.5rem 1.5rem;
    text-align: center;
    color: #64748b;
  }

  .api-error {
    margin: 1rem 0;
    padding: 0.85rem 1rem;
    border: 1px solid #fecaca;
    border-radius: 8px;
    background: #fef2f2;
    color: #991b1b;
    font-weight: 700;
  }
`

const normalizeSchedule = (item: any): WaveSchedule => {
  const targets = Array.isArray(item.targets)
    ? item.targets.map((target: any, index: number) => ({
        id: buildWaveScheduleTargetId(target.target_type, target.wave_profile_id || target.wave_directive_id || index),
        target_type: target.target_type,
        execution_order: target.execution_order ?? index + 1,
        label:
          target.label ||
          target.wave_profile_name ||
          target.wave_directive_name ||
          `Target ${target.wave_profile_id || target.wave_directive_id || index + 1}`,
        secondary_label:
          target.secondary_label ||
          (target.target_type === 'wave_profile'
            ? target.wave_profile_name || 'Wave Profile'
            : target.wave_profile_name || 'Wave Directive'),
        wave_profile_id: target.wave_profile_id ?? null,
        wave_directive_id: target.wave_directive_id ?? null,
        parent_profile_id: target.parent_profile_id ?? target.wave_profile_id ?? null,
      }))
    : []
  const calendarEntries: WaveScheduleCalendarEntry[] = Array.isArray(item.calendar_entries)
    ? item.calendar_entries.map((entry: any) => ({
        scheduled_at: entry.scheduled_at,
        scheduled_at_local: entry.scheduled_at_local || entry.scheduled_at,
        source: entry.source || 'projection',
        occurrence_id: entry.occurrence_id ?? null,
        status: entry.status || 'scheduled',
        downstream_run: entry.downstream_run || null,
        targets: Array.isArray(entry.targets)
          ? entry.targets.map((target: any) => ({
              target_type: target.target_type,
              target_id: Number(target.target_id),
              target_label: target.target_label || `Target ${target.target_id}`,
            }))
          : [],
      }))
    : []

  return {
    id: Number(item.id),
    name: item.name || item.recurrence_display || `Schedule ${item.id}`,
    is_active: Boolean(item.is_active),
    schedule_type: item.schedule_type || item.recurrence_type || 'daily',
    recurrence_interval: item.recurrence_interval || 1,
    execution_times: item.execution_times?.length ? item.execution_times : [item.time_of_day || '06:30:00'],
    days_of_week: item.days_of_week || null,
    date_rule: item.date_rule || null,
    day_of_month: item.day_of_month || null,
    week_segment: item.week_segment || null,
    day_of_week: item.day_of_week || null,
    targets,
    blackout_dates: item.blackout_dates || [],
    next_occurrence_at: item.next_occurrence_at || null,
    next_occurrence_local: item.next_occurrence_local || null,
    last_run_at: item.last_run_at || null,
    last_launch_status: item.last_launch_status || null,
    last_downstream_run: item.last_downstream_run || null,
    warehouse_timezone: item.warehouse_timezone || 'America/Los_Angeles',
    calendar_entries: calendarEntries,
    created_by: item.created_by,
    created: item.created,
    updated: item.updated,
    start_date: item.start_date || null,
    end_date: item.end_date || null,
  }
}

const normalizeOccurrence = (item: any): WaveScheduleOccurrence => {
  const targetResults = Array.isArray(item.target_results)
    ? item.target_results.map((target: any) => {
        const downstreamStatus = target.downstream_run?.status
        const displayStatus =
          downstreamStatus === 'completed' ||
          downstreamStatus === 'failed' ||
          downstreamStatus === 'running' ||
          downstreamStatus === 'queued'
            ? downstreamStatus
            : target.status || 'pending'
        const targetId = buildWaveScheduleTargetId(target.target_type, target.target_id)
        return {
          ...target,
          target_id: targetId,
          label: target.target_label || target.label || `Target ${target.target_id}`,
          status: displayStatus,
          launch_status: target.status,
          downstream_run: target.downstream_run || null,
        }
      })
    : []
  const downstreamRun =
    item.downstream_run || targetResults.find((target: any) => target.downstream_run)?.downstream_run

  return {
    id: Number(item.id),
    scheduled_at: item.scheduled_at,
    scheduled_at_local: item.scheduled_at_local || item.scheduled_at,
    status: item.status,
    skipped_by: item.skipped_by || null,
    launched_at: item.launched_at || null,
    launch_error_message: item.launch_error_message || null,
    downstream_run: downstreamRun || null,
    attempt_count:
      item.attempt_count ?? Math.max(0, ...targetResults.map((target: any) => Number(target.attempt_count) || 0)),
    target_summary: item.target_summary || '',
    target_results: targetResults,
  }
}

type WaveScheduleDashboardProps = {
  scheduleResponse?: any
  schedulesLoaded?: boolean
  schedulesError?: boolean
  profileResponse?: any
  profilesLoaded?: boolean
  profilesError?: boolean
  onSchedulesChange?: (schedules: WaveSchedule[]) => void
}

export const WaveScheduleDashboard = ({
  scheduleResponse: prefetchedScheduleResponse,
  schedulesLoaded: prefetchedSchedulesLoaded,
  schedulesError: prefetchedSchedulesError,
  profileResponse: prefetchedProfileResponse,
  profilesLoaded: prefetchedProfilesLoaded,
  profilesError: prefetchedProfilesError,
  onSchedulesChange,
}: WaveScheduleDashboardProps) => {
  const {
    state: { csrf, warehouseActive },
  } = globalState()
  const { hasAccess } = usePermissions()
  const [historySchedule, setHistorySchedule] = useState<WaveSchedule | null>(null)
  const [visibleDate, setVisibleDate] = useState(new Date())
  const [calendarViewMode, setCalendarViewMode] = useState<'week' | 'month'>('week')
  const [timeDisplayMode, setTimeDisplayMode] = useState<ScheduleTimeDisplayMode>('warehouse')
  const viewerTimezone = useMemo(() => getViewerTimezone(), [])
  const usePrefetchedSchedules =
    prefetchedScheduleResponse !== undefined ||
    prefetchedSchedulesLoaded !== undefined ||
    prefetchedSchedulesError !== undefined
  const usePrefetchedProfiles = prefetchedProfileResponse !== undefined || prefetchedProfilesLoaded !== undefined
  const activeWarehouseId = warehouseActive && typeof warehouseActive === 'object' ? warehouseActive.id : null
  const { response: warehouseResponse }: any = useFetch(
    activeWarehouseId ? `/api/warehouses/${activeWarehouseId}/` : '',
    {}
  )
  const scheduleQueryRange = useMemo(() => {
    const weekStartsOn = { weekStartsOn: 1 as const }
    const rangeStart = startOfWeek(startOfMonth(visibleDate), weekStartsOn)
    const rangeEnd = endOfWeek(endOfMonth(visibleDate), weekStartsOn)
    return {
      startDate: format(rangeStart, 'yyyy-MM-dd'),
      endDate: format(rangeEnd, 'yyyy-MM-dd'),
    }
  }, [visibleDate])
  const initialPrefetchedRange = useMemo(() => {
    const today = new Date()
    const weekStartsOn = { weekStartsOn: 1 as const }
    return {
      startDate: format(startOfWeek(startOfMonth(today), weekStartsOn), 'yyyy-MM-dd'),
      endDate: format(endOfWeek(endOfMonth(today), weekStartsOn), 'yyyy-MM-dd'),
    }
  }, [])
  const prefetchedSchedulesConsumedRef = useRef(false)
  const shouldUsePrefetchedSchedules =
    usePrefetchedSchedules &&
    !prefetchedSchedulesConsumedRef.current &&
    scheduleQueryRange.startDate === initialPrefetchedRange.startDate &&
    scheduleQueryRange.endDate === initialPrefetchedRange.endDate
  const schedulesUrl = useMemo(() => {
    if (shouldUsePrefetchedSchedules) return ''
    return buildWaveScheduleListUrl(scheduleQueryRange.startDate, scheduleQueryRange.endDate)
  }, [scheduleQueryRange.endDate, scheduleQueryRange.startDate, shouldUsePrefetchedSchedules])

  const {
    response: localScheduleResponse,
    loaded: localSchedulesLoaded,
    error: localSchedulesError,
  }: any = useFetch(schedulesUrl, {})
  const {
    response: localProfileResponse,
    loaded: localProfilesLoaded,
    error: localProfilesError,
  }: any = useFetch(usePrefetchedProfiles ? '' : '/api/inventory/wave-profile/', {})
  const scheduleResponse = shouldUsePrefetchedSchedules ? prefetchedScheduleResponse : localScheduleResponse
  const schedulesLoaded = shouldUsePrefetchedSchedules ? Boolean(prefetchedSchedulesLoaded) : localSchedulesLoaded
  const schedulesError = shouldUsePrefetchedSchedules ? Boolean(prefetchedSchedulesError) : localSchedulesError
  const profileResponse = usePrefetchedProfiles ? prefetchedProfileResponse : localProfileResponse
  const profilesLoaded = usePrefetchedProfiles ? Boolean(prefetchedProfilesLoaded) : localProfilesLoaded
  const profilesError = usePrefetchedProfiles ? Boolean(prefetchedProfilesError) : Boolean(localProfilesError)

  const [schedules, setSchedules] = useState<WaveSchedule[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<WaveSchedule | null>(null)
  const [historyScheduledAt, setHistoryScheduledAt] = useState<string | null>(null)
  const [historyOrderEstimateLabel, setHistoryOrderEstimateLabel] = useState<string | null>(null)
  const [historyPage, setHistoryPage] = useState(1)
  const [historyPageSize, setHistoryPageSize] = useState(25)
  const [historyStatus, setHistoryStatus] = useState('all')
  const [skipOccurrenceInFlight, setSkipOccurrenceInFlight] = useState(false)
  const [nowTick, setNowTick] = useState(() => Date.now())
  const nowIso = useMemo(() => new Date(nowTick).toISOString(), [nowTick])
  const historyOffset = (historyPage - 1) * historyPageSize
  const historyQuery = new URLSearchParams()
  if (historyScheduledAt) {
    historyQuery.set('scheduled_at_gte', historyScheduledAt)
    historyQuery.set('scheduled_at_lte', historyScheduledAt)
    historyQuery.set('limit', '5')
  } else {
    historyQuery.set('limit', String(historyPageSize))
    historyQuery.set('offset', String(historyOffset))
    if (historyStatus !== 'all') historyQuery.set('status', historyStatus)
    historyQuery.set('scheduled_at_lte', nowIso)
  }
  const occurrencesUrl = historySchedule
    ? `/api/inventory/wave-schedules/${historySchedule.id}/occurrences/?${historyQuery.toString()}`
    : ''
  const {
    response: occurrencesResponse,
    responseUrl: occurrencesResponseUrl,
    loaded: occurrencesLoaded,
    error: occurrencesError,
  }: any = useFetch(occurrencesUrl, {})
  const [historyOccurrences, setHistoryOccurrences] = useState<WaveScheduleOccurrence[]>([])
  const [createPrefill, setCreatePrefill] = useState<Partial<WaveScheduleDraft> | null>(null)
  const [statusFilter, setStatusFilter] = useState('active')
  const [companyFilters, setCompanyFilters] = useState<CompanyFilterOption[]>([])
  const [scheduleFilter, setScheduleFilter] = useState<ScheduleFilterOption | null>(null)
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : null

  const apiSchedules = useMemo(() => {
    const rawResults = Array.isArray(scheduleResponse)
      ? scheduleResponse
      : Array.isArray(scheduleResponse?.results)
        ? scheduleResponse.results
        : null

    return rawResults ? rawResults.map(normalizeSchedule) : null
  }, [scheduleResponse])

  useEffect(() => {
    if (!apiSchedules) return
    setSchedules(apiSchedules)
    if (shouldUsePrefetchedSchedules) prefetchedSchedulesConsumedRef.current = true
  }, [apiSchedules, shouldUsePrefetchedSchedules])

  useEffect(() => {
    if (!historySchedule || !occurrencesResponse || occurrencesResponseUrl !== occurrencesUrl) return
    const rawResults = Array.isArray(occurrencesResponse)
      ? occurrencesResponse
      : Array.isArray(occurrencesResponse?.results)
        ? occurrencesResponse.results
        : []
    setHistoryOccurrences(rawResults.map(normalizeOccurrence))
  }, [historySchedule, occurrencesResponse, occurrencesResponseUrl, occurrencesUrl])

  useEffect(() => {
    const intervalId = window.setInterval(() => setNowTick(Date.now()), 60_000)
    return () => window.clearInterval(intervalId)
  }, [])

  const rawProfiles = useMemo(() => {
    if (Array.isArray(profileResponse)) return profileResponse
    if (Array.isArray(profileResponse?.results)) return profileResponse.results
    return []
  }, [profileResponse])

  const targetOptions: WaveScheduleTargetOption[] = useMemo(() => {
    if (!profilesLoaded) return []
    return normalizeTargetsFromProfiles(rawProfiles)
  }, [profilesLoaded, rawProfiles])

  const companyOptions = useMemo(() => {
    const options = new Map<string, CompanyFilterOption>()

    rawProfiles.forEach((profile: any) => {
      ;(profile.companies || []).forEach((company: any) => {
        const label = company?.label || company?.name
        if (!label) return
        const value = company?.value != null ? `id:${company.value}` : `label:${String(label).toLowerCase()}`
        if (!options.has(value)) {
          options.set(value, { value, label: String(label) })
        }
      })
    })

    return Array.from(options.values()).sort((left, right) => left.label.localeCompare(right.label))
  }, [rawProfiles])

  const scheduleOptions = useMemo(
    () =>
      schedules
        .map(({ id, name }) => ({ value: id, label: name }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [schedules]
  )

  const profileCompanyMap = useMemo(() => {
    const mapping = new Map<number, Set<string>>()

    rawProfiles.forEach((profile: any) => {
      const values = new Set<string>()
      ;(profile.companies || []).forEach((company: any) => {
        const label = company?.label || company?.name
        if (label) values.add(`label:${String(label).toLowerCase()}`)
        if (company?.value != null) values.add(`id:${company.value}`)
      })
      mapping.set(Number(profile.id), values)
    })

    return mapping
  }, [rawProfiles])

  const profileNamesById = useMemo(() => {
    const map: Record<number, string> = {}
    rawProfiles.forEach((profile: any) => {
      const id = Number(profile.id)
      if (!Number.isFinite(id)) return
      const companyLabels = Array.isArray(profile.companies)
        ? profile.companies.map(({ label }: { label: string }) => label).filter(Boolean)
        : []
      const companyLabel = companyLabels.join(', ')
      map[id] = companyLabel || profile.name || profile.profile || `Wave Profile ${id}`
    })
    return map
  }, [rawProfiles])

  const directivesByProfileId = useMemo(() => {
    const map: Record<number, { id: number; name: string; expected_order_count: number | null }[]> = {}
    rawProfiles.forEach((profile: any) => {
      const profileId = Number(profile.id)
      if (!Number.isFinite(profileId)) return
      map[profileId] = [...(profile.wave_directives || [])]
        .sort((left: any, right: any) => Number(left.index) - Number(right.index) || Number(left.id) - Number(right.id))
        .map((directive: any) => ({
          id: Number(directive.id),
          name: directive.name || `Wave Directive ${directive.id}`,
          expected_order_count:
            directive?.order_counts?.received != null ? Number(directive.order_counts.received) : null,
        }))
    })
    return map
  }, [rawProfiles])

  const expectedOrderCountsByScheduleId = useMemo(() => {
    const directiveCounts = new Map<number, number | null>()
    Object.values(directivesByProfileId).forEach((directives) => {
      directives.forEach((directive) => directiveCounts.set(directive.id, directive.expected_order_count))
    })

    return Object.fromEntries(
      schedules.map((schedule) => {
        const counts = schedule.targets.flatMap((target) => {
          if (target.wave_profile_id) {
            return (directivesByProfileId[target.wave_profile_id] || []).map(
              (directive) => directive.expected_order_count
            )
          }
          return target.wave_directive_id ? [directiveCounts.get(target.wave_directive_id) ?? null] : [null]
        })
        const validCounts = counts.filter((count): count is number => count != null && Number.isFinite(count))
        return [schedule.id, validCounts.length ? validCounts : null]
      })
    )
  }, [directivesByProfileId, schedules])

  const directivePreviewPayloadById = useMemo(() => {
    const map: Record<number, { settings: any; sort: any; filters: any }> = {}
    rawProfiles.forEach((profile: any) => {
      ;(profile.wave_directives || []).forEach((directive: any) => {
        const directiveId = Number(directive.id)
        if (!Number.isFinite(directiveId)) return
        map[directiveId] = {
          settings: directive.settings,
          sort: directive.sort,
          filters: directive.filters,
        }
      })
    })
    return map
  }, [rawProfiles])

  const canManageSchedules = hasAccess('outbound', 'admin')

  const calendarSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      const matchesSchedule = !scheduleFilter || schedule.id === scheduleFilter.value
      const matchesCompany =
        !companyFilters.length ||
        schedule.targets.some((target) => {
          const profileId = target.parent_profile_id || target.wave_profile_id
          if (!profileId) return false
          const companyValues = profileCompanyMap.get(profileId)
          return companyFilters.some(({ value }) => companyValues?.has(value))
        })

      return matchesSchedule && matchesCompany
    })
  }, [companyFilters, profileCompanyMap, scheduleFilter, schedules])
  const tableSchedules = useMemo(() => {
    return calendarSchedules.filter((schedule) => {
      return (
        statusFilter === 'all' ||
        (statusFilter === 'active' && schedule.is_active) ||
        (statusFilter === 'inactive' && !schedule.is_active)
      )
    })
  }, [calendarSchedules, statusFilter])

  const warehouseTimezone =
    schedules[0]?.warehouse_timezone || warehouseResponse?.timezone || warehouseActive?.timezone || 'UTC'
  const activeDisplayTimezone = getDisplayTimezone(warehouseTimezone, timeDisplayMode, viewerTimezone)
  const displayTodayDateKey = getTodayDateInTimezone(activeDisplayTimezone)
  const currentMinutesInDay = useMemo(
    () => getCurrentMinutesInTimezone(activeDisplayTimezone),
    [activeDisplayTimezone, nowTick]
  )

  const getCalendarEntries = useCallback(
    (rangeStart: Date, rangeEnd: Date) =>
      buildCalendarEntries(
        calendarSchedules,
        rangeStart,
        rangeEnd,
        timeDisplayMode,
        viewerTimezone,
        expectedOrderCountsByScheduleId,
        displayTodayDateKey
      ),
    [calendarSchedules, displayTodayDateKey, expectedOrderCountsByScheduleId, timeDisplayMode, viewerTimezone]
  )

  const formatNextOccurrenceDisplay = useCallback(
    (schedule: WaveSchedule) => {
      const scheduleWarehouseTimezone = schedule.warehouse_timezone || warehouseTimezone

      if (timeDisplayMode === 'warehouse') {
        if (schedule.next_occurrence_local) {
          return formatDateTime(schedule.next_occurrence_local, true, scheduleWarehouseTimezone)
        }
        return formatDateTime(schedule.next_occurrence_at, true, scheduleWarehouseTimezone)
      }

      if (schedule.next_occurrence_at) {
        return formatDateTime(schedule.next_occurrence_at, true, viewerTimezone)
      }

      if (schedule.next_occurrence_local) {
        const parsed = parseDateTimeInTimezone(schedule.next_occurrence_local, scheduleWarehouseTimezone)
        if (parsed?.isValid()) {
          return parsed.tz(viewerTimezone).format('ddd, MMM D, YYYY h:mm A')
        }
      }

      return 'Not scheduled'
    },
    [timeDisplayMode, viewerTimezone, warehouseTimezone]
  )

  const openCreate = (prefill?: Partial<WaveScheduleDraft>) => {
    setEditingSchedule(null)
    setCreatePrefill(prefill || null)
    setDrawerOpen(true)
  }

  const openEdit = (schedule: WaveSchedule) => {
    setEditingSchedule(schedule)
    setCreatePrefill(null)
    setDrawerOpen(true)
  }

  const openCreateAtSlot = (slot: ScheduleCalendarSlot) => {
    const normalizedSlot =
      timeDisplayMode === 'warehouse'
        ? { date: slot.date, time: slot.time }
        : convertDisplaySlotToWarehouse(slot.date, slot.time, viewerTimezone, warehouseTimezone)

    if (isWarehouseSlotInPast(normalizedSlot.date, normalizedSlot.time, warehouseTimezone)) {
      notify({ type: 'warning', message: 'Cannot create a schedule from a past time slot.' })
      return
    }

    openCreate({
      start_date: normalizedSlot.date,
      execution_times: [normalizedSlot.time],
    })
  }

  const openHistory = (schedule: WaveSchedule, scheduledAt?: string | null, orderEstimateLabel?: string | null) => {
    setHistorySchedule(schedule)
    setHistoryScheduledAt(scheduledAt || null)
    setHistoryOrderEstimateLabel(orderEstimateLabel || null)
    setHistoryPage(1)
    setHistoryStatus('all')
    setHistoryOccurrences([])
    setHistoryOpen(true)
  }

  const closeHistory = () => {
    setHistoryOpen(false)
    setHistorySchedule(null)
    setHistoryScheduledAt(null)
    setHistoryOrderEstimateLabel(null)
    setHistoryPage(1)
    setHistoryStatus('all')
    setHistoryOccurrences([])
  }

  const refetchSchedulesForVisibleRange = async (): Promise<WaveSchedule[] | null> => {
    try {
      const response = await fetch(buildWaveScheduleListUrl(scheduleQueryRange.startDate, scheduleQueryRange.endDate), {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrf,
        },
      })
      if (!response.ok) return null

      const payload = await response.json().catch(() => null)
      const rawResults = Array.isArray(payload) ? payload : Array.isArray(payload?.results) ? payload.results : null
      if (!rawResults) return null

      const refreshedSchedules: WaveSchedule[] = rawResults.map(normalizeSchedule)
      setSchedules(refreshedSchedules)
      onSchedulesChange?.(refreshedSchedules)
      return refreshedSchedules
    } catch {
      return null
    }
  }

  const saveSchedule = (schedule: WaveSchedule) => {
    const isExisting = schedules.some((existingSchedule) => existingSchedule.id === schedule.id)
    const resolvedStartDate =
      schedule.start_date || getTodayDateInTimezone(schedule.warehouse_timezone || warehouseTimezone)
    const todayInWarehouse = getTodayDateInTimezone(schedule.warehouse_timezone || warehouseTimezone)
    if (!isExisting && resolvedStartDate < todayInWarehouse) {
      notify({ type: 'warning', message: 'Start date cannot be in the past.' })
      return
    }
    const payload = {
      name: schedule.name,
      schedule_type: schedule.schedule_type,
      recurrence_interval: schedule.recurrence_interval,
      execution_times: schedule.execution_times,
      days_of_week: schedule.schedule_type === 'weekly' ? schedule.days_of_week : null,
      date_rule: schedule.schedule_type === 'monthly' ? schedule.date_rule : null,
      day_of_month:
        schedule.schedule_type === 'monthly' && schedule.date_rule === 'day_of_month' ? schedule.day_of_month : null,
      month_of_year: null,
      week_segment:
        schedule.schedule_type === 'monthly' && schedule.date_rule === 'month_segment' ? schedule.week_segment : null,
      day_of_week:
        schedule.schedule_type === 'monthly' && schedule.date_rule === 'month_segment' ? schedule.day_of_week : null,
      end_type: schedule.end_date ? 'date' : 'never',
      end_after_occurrences: null,
      start_date: resolvedStartDate,
      end_date: schedule.end_date,
      targets: schedule.targets.map((target, index) => ({
        wave_profile_id: target.target_type === 'wave_profile' ? target.wave_profile_id || null : null,
        wave_directive_id: target.target_type === 'wave_directive' ? target.wave_directive_id || null : null,
        execution_order: index + 1,
      })),
      blackout_dates: schedule.blackout_dates,
    }

    const request = isExisting
      ? usePut(`/api/inventory/wave-schedules/${schedule.id}/`, payload, csrf, false)
      : usePost('/api/inventory/wave-schedules/', payload, csrf, false)

    request.then(async (response) => {
      if (!response || response.error || !response.id) return
      const refreshed = await refetchSchedulesForVisibleRange()
      setDrawerOpen(false)
      if (!refreshed) {
        notify({ type: 'warning', message: 'Schedule saved, but the calendar could not be refreshed.' })
      }
    })
  }

  const toggleSchedule = (schedule: WaveSchedule) => {
    const url = `/api/inventory/wave-schedules/${schedule.id}/${schedule.is_active ? 'deactivate' : 'activate'}/`
    usePost(url, {}, csrf, false).then(async (response) => {
      if (!response || response.error || !response.id) return
      const refreshed = await refetchSchedulesForVisibleRange()
      if (!refreshed) {
        notify({ type: 'warning', message: 'Schedule updated, but the calendar could not be refreshed.' })
      }
    })
  }

  const deleteSchedule = (schedule: WaveSchedule) => {
    useDelete(`/api/inventory/wave-schedules/${schedule.id}/`, csrf, false, true, true, async (response: Response) => {
      if (response.status === 204) {
        const remainingSchedules = schedules.filter(({ id }) => id !== schedule.id)
        setSchedules(remainingSchedules)
        onSchedulesChange?.(remainingSchedules)
        return { success: true }
      }
      const data = await response.json().catch(() => null)
      if (data?.error) {
        notify({ type: 'warning', message: data.error })
      }
      return data
    })
  }

  const selectedOccurrences: WaveScheduleOccurrence[] = historySchedule ? historyOccurrences : []

  const normalizeCalendarEntryFromOccurrence = useCallback(
    (schedule: WaveSchedule, occurrence: WaveScheduleOccurrence): WaveScheduleCalendarEntry => ({
      scheduled_at: occurrence.scheduled_at,
      scheduled_at_local: occurrence.scheduled_at_local,
      source: 'occurrence',
      occurrence_id: occurrence.id,
      status: occurrence.status,
      targets:
        occurrence.target_results?.map((target) => ({
          target_type: target.target_type || 'wave_directive',
          target_id: Number(String(target.target_id).split('-').pop()),
          target_label: target.label,
        })) ||
        schedule.targets.map((target) => ({
          target_type: target.target_type,
          target_id: target.wave_profile_id || target.wave_directive_id || 0,
          target_label: target.label,
        })),
    }),
    []
  )

  const skipOccurrence = async (schedule: WaveSchedule, scheduledAt: string) => {
    setSkipOccurrenceInFlight(true)
    try {
      const response = await usePost(
        `/api/inventory/wave-schedules/${schedule.id}/skip-occurrence/`,
        { scheduled_at: scheduledAt },
        csrf,
        false
      )

      if (!response || response.error || !response.id) {
        if (response?.error) notify({ type: 'warning', message: response.error })
        return
      }

      const skippedOccurrence = normalizeOccurrence(response)
      const skippedCalendarEntry = normalizeCalendarEntryFromOccurrence(schedule, skippedOccurrence)
      const skippedScheduledAt = new Date(skippedCalendarEntry.scheduled_at).getTime()
      const updatedSchedules = schedules.map((currentSchedule) => {
        if (currentSchedule.id !== schedule.id) return currentSchedule
        const withoutDuplicate = currentSchedule.calendar_entries.filter(
          (entry) =>
            entry.occurrence_id !== skippedCalendarEntry.occurrence_id &&
            new Date(entry.scheduled_at).getTime() !== skippedScheduledAt
        )
        const merged = [skippedCalendarEntry].concat(withoutDuplicate)
        merged.sort((left, right) => left.scheduled_at.localeCompare(right.scheduled_at))
        return { ...currentSchedule, calendar_entries: merged }
      })
      setSchedules(updatedSchedules)
      onSchedulesChange?.(updatedSchedules)
      setHistoryOccurrences((current) => {
        if (historySchedule?.id !== schedule.id) return current
        const withoutDuplicate = current.filter((occurrence) => occurrence.id !== skippedOccurrence.id)
        const merged = [skippedOccurrence].concat(withoutDuplicate)
        merged.sort((left, right) => right.scheduled_at.localeCompare(left.scheduled_at))
        return merged
      })
      notify({ type: 'success', message: 'Occurrence skipped' })
    } finally {
      setSkipOccurrenceInFlight(false)
    }
  }

  const unskipOccurrence = async (schedule: WaveSchedule, scheduledAt: string) => {
    setSkipOccurrenceInFlight(true)
    try {
      const response = await usePost(
        `/api/inventory/wave-schedules/${schedule.id}/unskip-occurrence/`,
        { scheduled_at: scheduledAt },
        csrf,
        false
      )

      if (!response || response.error) {
        if (response?.error) notify({ type: 'warning', message: response.error })
        return
      }

      const refreshed = await refetchSchedulesForVisibleRange()
      if (!refreshed) {
        notify({ type: 'warning', message: 'Occurrence was unskipped, but the calendar could not be refreshed.' })
        return
      }
      setHistoryOccurrences((currentOccurrences) => removeManualSkipOccurrence(currentOccurrences, scheduledAt))
      const refreshedHistorySchedule = refreshed.find(({ id }) => id === schedule.id)
      if (refreshedHistorySchedule) setHistorySchedule(refreshedHistorySchedule)
      notify({ type: 'success', message: 'Occurrence unskipped' })
    } finally {
      setSkipOccurrenceInFlight(false)
    }
  }

  if (!schedulesLoaded && !schedulesError && !schedules.length) {
    return <CardLoading />
  }

  return (
    <DashboardLayout>
      <GlobalStyles.CardHeader className="page-header">
        <GlobalStyles.CardTitle>
          <h3>Schedules</h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          <div className="page-actions">
            <Select
              className="filter-field"
              isClearable
              isMulti
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              styles={{
                menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
                menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
              }}
              options={companyOptions}
              placeholder="Filter by customer..."
              value={companyFilters}
              onChange={(selected: any) => setCompanyFilters(selected ? [...selected] : [])}
            />
            <Select
              className="filter-field"
              isClearable
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              styles={{
                menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
                menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
              }}
              options={scheduleOptions}
              placeholder="Filter by schedule..."
              value={scheduleFilter}
              onChange={(selected: any) => setScheduleFilter(selected || null)}
            />
            <PermissionGate segment="outbound" minLevel="admin">
              <GlobalStyles.Button type="button" onClick={() => openCreate()}>
                <FontAwesomeIcon icon={faPlus} /> Create Schedule
              </GlobalStyles.Button>
            </PermissionGate>
          </div>
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>

      {schedulesError ? (
        <div className="api-error" role="alert">
          Schedules could not be loaded. Refresh the page or try again; no mock data has been substituted.
        </div>
      ) : null}

      <ScheduleCalendar
        getEntries={getCalendarEntries}
        visibleDate={visibleDate}
        setVisibleDate={setVisibleDate}
        viewMode={calendarViewMode}
        setViewMode={setCalendarViewMode}
        allowFullscreen
        weekDescription=""
        monthDescription=""
        timezoneMode={timeDisplayMode}
        onTimezoneModeChange={(mode: ScheduleCalendarTimezoneMode) => setTimeDisplayMode(mode)}
        warehouseTimezoneLabel={warehouseTimezone}
        viewerTimezoneLabel={viewerTimezone}
        dayKeyTimezone={activeDisplayTimezone}
        todayDateKey={displayTodayDateKey}
        currentMinutesInDay={currentMinutesInDay}
        canMutate={canManageSchedules}
        onCreateSlot={openCreateAtSlot}
        onSelectEntry={(scheduleId, entry) => {
          const selectedSchedule = schedules.find(({ id }) => id === Number(scheduleId))
          if (selectedSchedule) openHistory(selectedSchedule, entry.scheduledAt, entry.orderEstimateLabel)
        }}
      />

      <div className="table-card">
        <div className="table-header">
          <div className="table-title">
            <h4>Schedule Manager</h4>
            <p>Monitor recurring runs, statuses, and schedule windows.</p>
          </div>
          <div className="table-actions">
            <div className="filter-group" style={{ minWidth: 200 }}>
              <label className="visually-hidden">Show status</label>
              <Form.Select
                value={statusFilter}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(event.target.value)}
              >
                <option value="all">All schedules</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </div>
          </div>
        </div>

        {tableSchedules.length ? (
          <GlobalStyles.DataTable>
            <thead>
              <tr>
                <th>
                  <span className="text center">Schedule</span>
                </th>
                <th>
                  <span className="text center">Scheduled Items</span>
                </th>
                <th>
                  <span className="text center">Recurrence</span>
                </th>
                <th>
                  <span className="text center">Next Local Run</span>
                </th>
                <th>
                  <span className="text center">Latest Outcome</span>
                </th>
                <th>
                  <span className="text center">Status</span>
                </th>
                <th>
                  <span className="text center">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tableSchedules.map((schedule) => {
                const status = getLatestOutcome(schedule)
                return (
                  <GlobalStyles.TableRow key={schedule.id}>
                    <TableCell>
                      <div className="schedule-name-row">
                        <div className="schedule-name">{schedule.name}</div>
                      </div>
                      <div className="schedule-meta">{summarizeTargets(schedule.targets)}</div>
                    </TableCell>
                    <TableCell>
                      <div className="target-list">
                        <div className="target-scroll">
                          {schedule.targets
                            .slice()
                            .sort((left, right) => left.execution_order - right.execution_order)
                            .map((target) => (
                              <div className="target-item" key={target.id}>
                                <span className="target-index">{target.execution_order}</span>
                                <div className="target-item-body">
                                  <div className="target-item-name">{target.label}</div>
                                  <span
                                    className={`target-type-pill ${
                                      target.target_type === 'wave_profile' ? 'profile' : 'directive'
                                    }`}
                                  >
                                    {target.target_type === 'wave_profile' ? 'Profile' : 'Directive'}
                                  </span>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell center>
                      <div>{formatRecurrence(schedule)}</div>
                      <div className="schedule-meta">Window: {formatScheduleWindow(schedule)}</div>
                    </TableCell>
                    <TableCell center>
                      {schedule.is_active ? (
                        <span className="next-run">{formatNextOccurrenceDisplay(schedule)}</span>
                      ) : (
                        <span className="next-run-muted">—</span>
                      )}
                    </TableCell>
                    <TableCell center>
                      {status ? (
                        <span className={`outcome-pill ${status.tone}`}>{status.label}</span>
                      ) : (
                        <div className="schedule-meta">No occurrences yet</div>
                      )}
                    </TableCell>
                    <TableCell center>
                      <StatusBoxStyled className={schedule.is_active ? 'green' : 'silver'}>
                        {schedule.is_active ? 'Active' : 'Inactive'}
                      </StatusBoxStyled>
                    </TableCell>
                    <TableCell center>
                      <div className="action-row">
                        <button className="action-button" type="button" onClick={() => openHistory(schedule)}>
                          <FontAwesomeIcon icon={faHistory} /> History
                        </button>
                        <PermissionGate segment="outbound" minLevel="admin">
                          <>
                            <button className="action-button" type="button" onClick={() => openEdit(schedule)}>
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <button className="action-button" type="button" onClick={() => toggleSchedule(schedule)}>
                              <FontAwesomeIcon icon={schedule.is_active ? faToggleOn : faToggleOff} />{' '}
                              {schedule.is_active ? 'Disable' : 'Enable'}
                            </button>
                            <button className="action-button" type="button" onClick={() => deleteSchedule(schedule)}>
                              <FontAwesomeIcon icon={faTrashAlt} /> Delete
                            </button>
                          </>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </GlobalStyles.TableRow>
                )
              })}
            </tbody>
          </GlobalStyles.DataTable>
        ) : (
          <div className="empty-state">
            <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '1.6rem', marginBottom: '0.75rem' }} />
            <div style={{ color: '#334155', fontWeight: 700 }}>No schedules match the current filters.</div>
            <div style={{ marginTop: '0.35rem' }}>
              Adjust the search or status filter, or create the first recurring schedule for this warehouse.
            </div>
          </div>
        )}
      </div>

      <WaveScheduleDrawer
        open={drawerOpen}
        schedule={editingSchedule}
        initialDraft={createPrefill}
        targetOptions={targetOptions}
        targetsLoading={!profilesLoaded && !profilesError}
        targetsError={profilesError}
        warehouseTimezone={warehouseTimezone}
        onClose={() => setDrawerOpen(false)}
        onSave={saveSchedule}
      />

      <WaveScheduleHistory
        open={historyOpen}
        onClose={closeHistory}
        schedule={historySchedule}
        occurrences={selectedOccurrences}
        loading={!occurrencesLoaded && !occurrencesError}
        error={occurrencesError}
        page={historyPage}
        count={Number(occurrencesResponse?.count) || 0}
        pageSize={historyPageSize}
        hasNext={Boolean(occurrencesResponse?.next)}
        hasPrevious={Boolean(occurrencesResponse?.previous) || historyPage > 1}
        statusFilter={historyStatus}
        onStatusFilterChange={(status) => {
          setHistoryStatus(status)
          setHistoryPage(1)
        }}
        onPageChange={setHistoryPage}
        onPageSizeChange={(nextSize) => {
          setHistoryPageSize(nextSize)
          setHistoryPage(1)
        }}
        selectedScheduledAt={historyScheduledAt}
        selectedOrderEstimateLabel={historyOrderEstimateLabel}
        timeDisplayMode={timeDisplayMode}
        viewerTimezone={viewerTimezone}
        skipInFlight={skipOccurrenceInFlight}
        onSkipOccurrence={skipOccurrence}
        onUnskipOccurrence={unskipOccurrence}
        profileNamesById={profileNamesById}
        directivesByProfileId={directivesByProfileId}
        directivePreviewPayloadById={directivePreviewPayloadById}
        profilesLoading={!profilesLoaded && !profilesError}
      />
    </DashboardLayout>
  )
}
